import { FC, useId, useMemo } from "react";

import { Box, BoxProps, SxProps, Theme, useTheme } from "@mui/material";
import { ChartDataProvider } from "@mui/x-charts/ChartDataProvider";
import { ChartsAxis } from "@mui/x-charts/ChartsAxis";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { ChartsClipPath } from "@mui/x-charts/ChartsClipPath";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsOverlay } from "@mui/x-charts/ChartsOverlay";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltipContainer } from "@mui/x-charts/ChartsTooltip";
import { ChartsWrapper } from "@mui/x-charts/ChartsWrapper";
import { AreaPlot, LineHighlightPlot, LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { DateTime } from "luxon";

import { ChartSeriesTooltip } from "./ChartSeriesTooltip";
import { DEFAULT_CHART_SIZE } from "./defaults";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getSkeletonColor, skeletonPulseAnimation } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type SeriesOptions = {
  color: string;
  data: (number | null)[];
  label: string;
  valueFormatter?: (value: number | null) => string;
  areaColor?: string;
  lineColor?: string;
};

type SimpleDataset = SeriesOptions & { id: string };

type MultiDataset = Record<string, SeriesOptions>;
export type DashboardLineChartProps = {
  xAxis:
    | {
        scaleType: "time";
        data: Date[];
        valueFormatter?: (value: Date) => string;
        tooltipValueFormatter?: (value: Date) => string;
        domainLimit?: "nice" | "strict";
      }
    | {
        scaleType: undefined;
        data: number[];
        valueFormatter?: (value: number) => string;
        tooltipValueFormatter?: (value: number) => string;
      };
  yAxis?: {
    min?: number;
    max?: number;
    tickMinStep?: number;
    width?: number;
    valueFormatter?: (value: number | null) => string;
    tooltipValueFormatter?: (value: number | null) => string;
  };
  dataset: SimpleDataset | MultiDataset;
  height?: number;
  sx?: SxProps<Theme>;
  chartMinWidth?: BoxProps["minWidth"];
  hideXAxisTicks?: boolean;
  stacked?: boolean;
  tooltipShowTotal?: boolean;
  tooltipTotalValueFormatter?: (value: number) => string;
  emphasizeHighlight?: boolean;
  curve?: "monotoneX" | "linear";
  hideHighlightMarks?: boolean;
};

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;
const MAX_STRICT_TICKS = 6;
// Room above the drawing area so a line on the top of the axis keeps the upper half of its stroke.
const LINE_STROKE_OVERFLOW = 2;

export const DashboardLineChart = ({
  dataset,
  xAxis,
  yAxis = {},
  height = DEFAULT_CHART_SIZE,
  sx,
  chartMinWidth,
  hideXAxisTicks,
  stacked = false,
  tooltipShowTotal,
  tooltipTotalValueFormatter,
  emphasizeHighlight,
  curve = "monotoneX",
  hideHighlightMarks = false,
}: DashboardLineChartProps) => {
  const theme = useTheme();
  const { t } = useTranslate();

  const series = useMemo(() => {
    if ("id" in dataset) {
      const simpleDataset = dataset as SimpleDataset;
      return [
        {
          type: "line" as const,
          id: simpleDataset.id,
          color: simpleDataset.color,
          data: simpleDataset.data,
          label: simpleDataset.label,
          curve,
          valueFormatter: simpleDataset.valueFormatter ?? yAxis.valueFormatter ?? ((x) => x?.toString() ?? ""),
          ...getAreaSeriesOptions(simpleDataset),
          ...(stacked && { stack: "total" }),
        },
      ];
    }

    return Object.entries(dataset as MultiDataset)
      .map(([id, seriesData]) => ({
        type: "line" as const,
        id,
        color: seriesData.color,
        data: seriesData.data,
        label: seriesData.label,
        curve,
        valueFormatter: seriesData.valueFormatter ?? yAxis.valueFormatter ?? ((x) => x?.toString() ?? ""),
        ...getAreaSeriesOptions(seriesData),
        ...(stacked && { stack: "total" }),
      }))
      .reverse(); // 1st bar declared should be on top
  }, [dataset, yAxis.valueFormatter, stacked, curve]);

  const styles = mergeSx(
    {
      position: "relative",
      width: "100%",
      height,
      overflowX: "auto",
      scrollbarColor: `${theme.palette.surface.brand.moderate} transparent`,
      ["&::-webkit-scrollbar-track"]: {
        background: "transparent",
      },
      ["&::-webkit-scrollbar-thumb"]: {
        background: theme.palette.surface.brand.moderate,
      },
    },
    sx
  );

  const clipPathId = useId();

  const seriesEntries: [string, SeriesOptions][] =
    "id" in dataset
      ? [[(dataset as SimpleDataset).id, dataset as SimpleDataset]]
      : Object.entries(dataset as MultiDataset);
  const areaColorSx = Object.fromEntries(
    seriesEntries
      .filter(([, seriesData]) => Boolean(seriesData.areaColor))
      .map(([id, seriesData]) => [`& .MuiAreaElement-series-${id}`, { fill: seriesData.areaColor, filter: "none" }])
  );

  const lineColorSx = Object.fromEntries(
    seriesEntries
      .filter(([, seriesData]) => Boolean(seriesData.lineColor))
      .map(([id, seriesData]) => [`& .MuiLineElement-series-${id}`, { stroke: seriesData.lineColor }])
  );

  const isEmpty =
    "id" in dataset
      ? (dataset as SimpleDataset).data.every((data) => data === null)
      : Object.values(dataset as MultiDataset).every((data) => data.data.every((data) => data === null));

  return (
    <Box sx={styles}>
      <Box height="100%" minWidth={isEmpty ? undefined : chartMinWidth} marginInlineStart={-3}>
        <ChartDataProvider
          xAxis={[
            {
              position: "bottom",
              data: xAxis.data,
              scaleType: xAxis.scaleType,
              valueFormatter:
                xAxis.valueFormatter ??
                (xAxis.scaleType === "time"
                  ? (value: Date) =>
                      DateTime.fromJSDate(value).toLocaleString({
                        ...DateTime.DATE_MED,
                        year: undefined,
                      })
                  : undefined),
              tickMinStep: xAxis.scaleType === "time" ? MILLISECONDS_IN_DAY : undefined,
              domainLimit: xAxis.scaleType === "time" ? xAxis.domainLimit : undefined,
              tickInterval:
                xAxis.scaleType === "time" && xAxis.domainLimit === "strict"
                  ? getEvenlySpacedTicks(xAxis.data)
                  : undefined,
            },
          ]}
          yAxis={[{ position: "left", ...yAxis }]}
          series={series}
        >
          <ChartsWrapper>
            <ChartsSurface
              sx={{
                "& .MuiChartsGrid-line": { stroke: theme.palette.border.neutral.quiet, strokeDasharray: 4 },
                "& .MuiChartsAxis-directionX .MuiChartsAxis-tick": hideXAxisTicks
                  ? { display: "none" }
                  : { stroke: theme.palette.border.neutral.moderate },
                "& .MuiChartsAxis-directionY .MuiChartsAxis-tick": { display: "none" },
                "& .MuiChartsAxis-line": { display: "none" },
                "& .MuiChartsAxisHighlight-root": emphasizeHighlight
                  ? { stroke: theme.palette.border.neutral.intense, strokeWidth: 2, strokeDasharray: "none" }
                  : { stroke: theme.palette.border.neutral.moderate },
                ...areaColorSx,
                ...lineColorSx,
                ...(isEmpty && !yAxis
                  ? {
                      "& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel": { display: "none" },
                    }
                  : {}),
              }}
            >
              <ChartsGrid horizontal />
              <ChartsAxis />
              <g clipPath={`url(#${clipPathId})`}>
                {/* Elements clipped inside the drawing area. */}
                <AreaPlot />
                <LinePlot />
                <ChartsOverlay />
                {!isEmpty && <ChartsAxisHighlight x="line" />}
              </g>
              <g data-drawing-container>
                {/* Elements able to overflow the drawing area. */}
                <MarkPlot />
                {!isEmpty && (
                  <ChartsTooltipContainer trigger="axis">
                    <ChartSeriesTooltip
                      xAxisValueFormatter={xAxis.tooltipValueFormatter}
                      yAxisValueFormatter={yAxis.tooltipValueFormatter}
                      showTotal={tooltipShowTotal}
                      totalValueFormatter={tooltipTotalValueFormatter}
                    />
                  </ChartsTooltipContainer>
                )}
              </g>
              {!hideHighlightMarks && <LineHighlightPlot />}
              <ChartsClipPath id={clipPathId} offset={{ top: LINE_STROKE_OVERFLOW }} />
            </ChartsSurface>
          </ChartsWrapper>
        </ChartDataProvider>
      </Box>
      {isEmpty && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            translate: "-50% calc(-50% - 13px)",
            padding: theme.spacing(1, 3),
            borderRadius: 1,
            backgroundColor: theme.palette.surface.neutral.white,
            border: `1px solid ${theme.palette.border.neutral.quiet}`,
          }}
        >
          <DashboardTypography variant="subtitle2">{t("common", "chart.noData")}</DashboardTypography>
        </Box>
      )}
    </Box>
  );
};

const getAreaSeriesOptions = ({ areaColor, data }: SeriesOptions) =>
  areaColor ? { area: true, showMark: data.filter((value) => value !== null).length <= 1 } : {};

type DashboardLineChartSkeletonProps = Pick<DashboardLineChartProps, "height" | "sx">;

export const DashboardLineChartSkeleton: FC<DashboardLineChartSkeletonProps> = ({
  height = DEFAULT_CHART_SIZE,
  sx,
}) => {
  const theme = useTheme();

  const styles = mergeSx(
    {
      width: "100%",
      height,
      pointerEvents: "none",
    },
    sx
  );

  const xAxis = { data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] };
  const series = [{ type: "line" as const, showMark: true, data: [1, 3, 5, 7.5, 5.5, 4, 7, 10, 5, 2] }];

  const clipPathId = useId();

  return (
    <Box sx={styles}>
      <Box height="100%" marginInlineStart={-3}>
        <ChartDataProvider
          xAxis={[
            {
              position: "bottom",
              data: xAxis.data,
              tickMinStep: 1,
            },
          ]}
          yAxis={[{ position: "left", min: 0, max: 10 }]}
          series={series}
        >
          <ChartsWrapper>
            <ChartsSurface
              sx={{
                "& .MuiChartsGrid-line": { stroke: theme.palette.border.neutral.quiet, strokeDasharray: 4 },
                "& .MuiChartsAxis-directionX .MuiChartsAxis-tick": { stroke: theme.palette.border.neutral.moderate },
                "& .MuiChartsAxis-directionY .MuiChartsAxis-tick": { display: "none" },
                "& .MuiChartsAxis-tickLabel": { display: "none" },
                "& .MuiChartsAxis-line": { display: "none" },
                "& .MuiLineElement-root": {
                  stroke: getSkeletonColor(theme),
                  animation: skeletonPulseAnimation,
                },
                "& .MuiMarkElement-root": {
                  stroke: getSkeletonColor(theme),
                },
              }}
            >
              <ChartsGrid horizontal />
              <ChartsAxis />
              <g clipPath={`url(#${clipPathId})`}>
                {/* Elements clipped inside the drawing area. */}
                <LinePlot skipAnimation />
                <ChartsOverlay />
              </g>
              <g data-drawing-container>
                {/* Elements able to overflow the drawing area. */}
                <MarkPlot />
              </g>
              <LineHighlightPlot />
              <ChartsClipPath id={clipPathId} offset={{ top: LINE_STROKE_OVERFLOW }} />
            </ChartsSurface>
          </ChartsWrapper>
        </ChartDataProvider>
      </Box>
    </Box>
  );
};

const getEvenlySpacedTicks = (dates: Date[]): Date[] => {
  const step = Math.ceil(dates.length / MAX_STRICT_TICKS);
  return dates.filter((_, index) => (dates.length - 1 - index) % step === 0);
};
